import { CircularProgress, Grid } from '@material-ui/core';
import { useFormik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { extractErrorMessage } from '../helpers/apiUtils';
import GlimraStore from '../store/GlimraStore';

import Button from './helpers/Button';
import Filler from './helpers/Filler';
import StyledTextField from './helpers/StyledTextField';
import { snackRef } from './Snack';

interface ExistingUserRegistrationFormProps {
  loading: boolean;
  onUserRegistration: (email: string, password: string, verification_code?: string) => void;
  mobile: string;
}

interface UserFormValues {
  verification_code: string;
  password: string;
  confirmPassword: string;
  mobile: string;
  email: string;
}

const ExistingUserRegistrationForm = ({
  loading,
  onUserRegistration,
  mobile,
}: ExistingUserRegistrationFormProps) => {
  const { t } = useTranslation();

  const onSubmit = async (data: UserFormValues) => {
    try {
      await GlimraStore.setUserEmailPassword({
        ...data,
      });
      snackRef.current.updateSnack({
        variant: 'success',
        title: t('chooseSubscriptionStep2.createUserSuccess.title'),
        message: t('chooseSubscriptionStep2.createUserSuccess.message'),
      });
      onUserRegistration(data.email, data.password, data.verification_code);
    } catch (error) {
      snackRef.current.updateSnack({
        variant: 'error',
        title: t('chooseSubscriptionStep2.createUserError.title'),
        message: extractErrorMessage(error, t('chooseSubscriptionStep2.createUserError.message')),
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      verification_code: '',
      password: '',
      confirmPassword: '',
      mobile: mobile,
      email: '',
    },
    validationSchema: Yup.object().shape({
      verification_code: Yup.string().required(t('existingAdminRegistrationForm.codeRequired')),
      password: Yup.string().required(t('existingAdminRegistrationForm.password')),
      mobile: Yup.string().required(t('existingAdminRegistrationForm.mobileRequired')),
      email: Yup.string()
        .required(t('existingAdminRegistrationForm.emailRequired'))
        .email(t('existingAdminRegistrationForm.emailFormat')),
      confirmPassword: Yup.string()
        .required(t('existingAdminRegistrationForm.passwordRequired'))
        .oneOf(
          [Yup.ref('password'), null],
          t('existingAdminRegistrationForm.confirmPasswordMatch')
        ),
    }),
    onSubmit: onSubmit,
  });

  return (
    <>
      <Grid item xs={12}>
        {t('existingAdminRegistrationForm.writeCode')}
      </Grid>
      <Grid item xs={12}>
        <StyledTextField
          id="mobile"
          label={t('existingAdminRegistrationForm.mobileLabel')}
          value={formik.values.mobile}
          error={formik.errors.mobile}
          touched={formik.touched.mobile}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          disabled
          required
        />
      </Grid>
      <Grid item xs={12}>
        <StyledTextField
          id="verification_code"
          label={t('existingAdminRegistrationForm.codeLabel')}
          value={formik.values.verification_code}
          error={formik.errors.verification_code}
          touched={formik.touched.verification_code}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <StyledTextField
          id="email"
          label={t('existingAdminRegistrationForm.emailLabel')}
          value={formik.values.email}
          error={formik.errors.email}
          touched={formik.touched.email}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <StyledTextField
          id="password"
          label={t('existingAdminRegistrationForm.passwordLabel')}
          type="password"
          value={formik.values.password}
          error={formik.errors.password}
          touched={formik.touched.password}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <StyledTextField
          id="confirmPassword"
          label={t('existingAdminRegistrationForm.confirmPasswordLabel')}
          type="password"
          value={formik.values.confirmPassword}
          error={formik.errors.confirmPassword}
          touched={formik.touched.confirmPassword}
          onBlur={formik.handleBlur}
          onChange={formik.handleChange}
          required
        />
      </Grid>

      <Filler item />

      <Grid item xs={12}>
        <Button
          onClick={formik.handleSubmit}
          variant="contained"
          color="default"
          fullWidth
          disabled={loading}
          disableElevation
        >
          {loading ? (
            <CircularProgress size={25} />
          ) : (
            t('existingAdminRegistrationForm.completeFlowButton')
          )}
        </Button>
      </Grid>
    </>
  );
};

export default ExistingUserRegistrationForm;
